import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { MasterDataEntityType } from "@/lib/enums";
import { masterDataExtractedValueKeys, masterDataMappingKeys } from "@/lib/query";

/**
 * TanStack Query hooks for the master-data extraction cache
 * (`master_data_extracted_value`) — the candidate values harvested from
 * Contract Search results, since Coach Manager has no operation to list
 * master data directly. Mirrors `src/hooks/use-master-data-mappings.ts`'s
 * pattern.
 */

export interface ExtractedValue {
  value: string;
}

interface ExtractedValuesResponse {
  data: ExtractedValue[];
}

export interface SyncMasterDataMappingDraftsInput {
  sourceBuId: string;
  targetBuId: string;
  entityType: MasterDataEntityType;
}

export interface SyncMasterDataMappingDraftsResult {
  created: number;
}

async function extractErrorMessage(res: Response, fallback: string): Promise<string> {
  const json = await res.json().catch(() => null);
  return json?.error ?? fallback;
}

/** Searchable list of a BU's cached master-data values for one entity type — feeds the Ref combobox. */
export function useExtractedValues(params: {
  buInstanceId: string;
  entityType: MasterDataEntityType;
  search?: string;
}) {
  return useQuery({
    queryKey: masterDataExtractedValueKeys.list(params),
    queryFn: async (): Promise<ExtractedValuesResponse> => {
      const searchParams = new URLSearchParams({
        buInstanceId: params.buInstanceId,
        entityType: params.entityType,
      });
      if (params.search) searchParams.set("search", params.search);

      const res = await fetch(`/api/admin/master-data-extracted-values?${searchParams}`);
      if (!res.ok) throw new Error(await extractErrorMessage(res, "Failed to fetch values"));
      return res.json();
    },
    enabled: Boolean(params.buInstanceId),
  });
}

export function useSyncMasterDataMappingDrafts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      input: SyncMasterDataMappingDraftsInput,
    ): Promise<SyncMasterDataMappingDraftsResult> => {
      const res = await fetch("/api/admin/master-data-mappings/sync-drafts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, "Failed to sync new values"));
      return res.json();
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: masterDataMappingKeys.all });
      toast.success(
        result.created > 0 ? `Added ${result.created} new draft mapping(s)` : "No new values found",
      );
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to sync new values");
    },
  });
}
